/**
 * HabitLoopCard — First Week retention loop (Day-7 LTV multiplier).
 *
 * Seven pills across the top show locked/current/completed state; below,
 * a single Klein-blue CTA for today's action. Day 7 triggers a confetti
 * celebration and grants 1 week of Pro free.
 *
 * Auto-hides once all 7 days are complete AND pro week has been granted,
 * so the Dashboard stays clean for returning long-timers.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Lock, ArrowRight, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import api, { apiUrl } from '../utils/apiClient';

export default function HabitLoopCard() {
  const [data, setData] = useState(null);
  const [working, setWorking] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const res = await api.get(apiUrl('/habit-loop'));
      setData(res.data);
    } catch { /* silent — first-week only feature */ }
  };
  useEffect(() => { load(); }, []);

  // Hide once fully complete + Pro week claimed. Before that, even on
  // Day-8+ we still want to show the "claim 1 week of Pro" CTA.
  if (!data) return null;
  if (data.all_done && data.pro_week_granted) return null;

  const today = data.today;
  const progressPct = (data.completed_days.length / data.total_days) * 100;

  const handleActionClick = async () => {
    if (!today) return;
    // Day 7: everything is complete EXCEPT claiming Pro week.
    if (today.day === 7 && data.all_done && !data.pro_week_granted) {
      return claimProWeek();
    }
    // Optimistically mark complete, navigate to the route
    setWorking(true);
    try {
      await api.post(apiUrl(`/habit-loop/complete/${today.day}`));
      await load();
      if (today.day === 7) {
        // They've just completed the final day — fire confetti, then
        // chain the Pro-week claim in the same flow.
        confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
        await claimProWeek();
      } else {
        navigate(today.route);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not mark complete';
      toast.error(msg);
    } finally {
      setWorking(false);
    }
  };

  const claimProWeek = async () => {
    setWorking(true);
    try {
      const res = await api.post(apiUrl('/habit-loop/claim-pro-week'));
      if (!res.data.already_granted) {
        confetti({ particleCount: 200, spread: 90, origin: { y: 0.6 } });
        toast.success("You did it — 1 week of Pro unlocked 🎉");
      }
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not claim Pro week';
      toast.error(msg);
    } finally {
      setWorking(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] p-5 sm:p-6"
      data-testid="habit-loop-card"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-[#002FA7] shrink-0" />
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--isp-muted)]">
            First Week Habit
          </p>
        </div>
        <p className="text-xs text-[var(--isp-muted)] shrink-0" data-testid="habit-loop-progress-text">
          {data.completed_days.length}/{data.total_days} done
        </p>
      </div>

      {/* Progress bar (thin, Klein blue) */}
      <div className="h-1 bg-[var(--isp-glass-inner)] mb-5 overflow-hidden">
        <motion.div
          className="h-full bg-[#002FA7]"
          initial={{ width: 0 }}
          animate={{ width: `${progressPct}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      {/* 7 day pills */}
      <div className="grid grid-cols-7 gap-1.5 mb-6">
        {data.days.map((d) => {
          const state = d.completed
            ? 'done'
            : d.is_today
              ? 'today'
              : d.locked
                ? 'locked'
                : 'open';
          const base = 'aspect-square flex items-center justify-center text-[11px] font-bold transition-all';
          const styles = {
            done: 'bg-[#002FA7] text-white',
            today: 'bg-[var(--isp-page)] border-2 border-[#002FA7] text-[#002FA7] animate-pulse',
            open: 'bg-[var(--isp-glass-inner)] text-[var(--isp-body)]',
            locked: 'bg-[var(--isp-glass-inner)] text-[var(--isp-muted)] opacity-50',
          };
          return (
            <div key={d.day} className={`${base} ${styles[state]}`}
              data-testid={`habit-day-pill-${d.day}`}
              title={d.title}
            >
              {state === 'done' ? <Check className="w-3.5 h-3.5" />
                : state === 'locked' ? <Lock className="w-3 h-3" />
                  : d.day}
            </div>
          );
        })}
      </div>

      {/* Today's action */}
      {today && (
        <div className="min-w-0" data-testid="habit-today-block">
          <p
            className="text-xs font-semibold uppercase tracking-[0.14em] text-[#002FA7] mb-1"
            data-testid="habit-today-eyebrow"
          >
            Day {today.day} of {data.total_days}
          </p>
          <h3
            className="text-lg sm:text-xl font-bold text-[var(--isp-heading)] tracking-tight mb-1"
            data-testid="habit-today-title"
          >
            {today.title}
          </h3>
          <p className="text-sm text-[var(--isp-body)] mb-4">
            {today.subtitle}
          </p>
          <button
            onClick={handleActionClick}
            disabled={working}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#002FA7] hover:bg-[#00227A] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            data-testid="habit-today-cta"
          >
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                {today.action_label}
                <ArrowRight className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </div>
      )}

      {/* All-done but Pro week not yet claimed */}
      {!today && data.all_done && !data.pro_week_granted && (
        <div className="min-w-0" data-testid="habit-claim-block">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#002FA7] mb-1">
            All 7 days complete
          </p>
          <h3 className="text-lg sm:text-xl font-bold text-[var(--isp-heading)] tracking-tight mb-1">
            You've built a habit 💪
          </h3>
          <p className="text-sm text-[var(--isp-body)] mb-4">
            Claim your free week of Pro.
          </p>
          <button
            onClick={claimProWeek}
            disabled={working}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#002FA7] hover:bg-[#00227A] disabled:opacity-60 text-white text-sm font-semibold transition-colors"
            data-testid="habit-claim-pro-btn"
          >
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <>
                Claim 1 week of Pro
                <Sparkles className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </div>
      )}
    </motion.div>
  );
}
